import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { NoAccessDialogComponent } from '../../components/no-access-dialog/no-access-dialog.component';
import { LoginService } from '../login/login.service';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class TokenExpirationService implements OnDestroy {
  private readonly TOKEN_EXPIRATION_HOURS = 3;
  private readonly TOKEN_DATE_KEY = 'tokenDate';
  private timeoutId: number | null = null;
  private isHandlingExpiration = false;
  private isManualLogout = false;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loginService: LoginService,
    private storageService: StorageService
  ) {}

  ngOnDestroy(): void {
    this.clearExpirationTimeout();
  }

  /**
   * Checks if the current token has expired (2 hours since login)
   * @returns true if token is expired, false otherwise
   */
  isTokenExpired(): boolean {
    const tokenDateStr = localStorage.getItem(this.TOKEN_DATE_KEY);

    if (!tokenDateStr) {
      return true; // No token date means expired
    }

    try {
      const tokenDate = new Date(tokenDateStr);
      const now = new Date();
      const hoursDifference =
        (now.getTime() - tokenDate.getTime()) / (1000 * 60 * 60);

      return hoursDifference >= this.TOKEN_EXPIRATION_HOURS;
    } catch (error) {
      console.error('Error parsing token date:', error);
      return true; // If we can't parse the date, consider it expired
    }
  }

  /**
   * Gets the remaining time until token expiration in milliseconds
   * @returns remaining time in milliseconds, or 0 if expired
   */
  getRemainingTokenTime(): number {
    const tokenDateStr = localStorage.getItem(this.TOKEN_DATE_KEY);

    if (!tokenDateStr) {
      return 0;
    }

    try {
      const tokenDate = new Date(tokenDateStr);
      const expirationDate = new Date(
        tokenDate.getTime() + this.TOKEN_EXPIRATION_HOURS * 60 * 60 * 1000
      );
      const now = new Date();

      const remainingTime = expirationDate.getTime() - now.getTime();
      return Math.max(0, remainingTime);
    } catch (error) {
      console.error('Error calculating remaining token time:', error);
      return 0;
    }
  }

  /**
   * Updates the token date to the current time
   */
  updateTokenDate(): void {
    localStorage.setItem(this.TOKEN_DATE_KEY, new Date().toString());
  }

  /**
   * Clears any existing expiration timeout
   */
  private clearExpirationTimeout(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Stops token expiration monitoring
   * Should be called during logout or when no longer needed
   */
  stopTokenExpirationMonitoring(): void {
    this.clearExpirationTimeout();
    this.isHandlingExpiration = false;
    this.isManualLogout = true; // Mark as manual logout to prevent expiration dialog
    // console.log('Token expiration monitoring stopped');
  }

  /**
   * Handles token expiration by logging out the user and showing a message
   */
  handleTokenExpiration(): void {
    // Prevent multiple simultaneous executions
    if (this.isHandlingExpiration) {
      return;
    }

    this.isHandlingExpiration = true;
    //console.log('Token has expired, logging out user');

    // Clear any existing timeout
    this.clearExpirationTimeout();

    // Clear all session data
    localStorage.clear();
    this.storageService.clear();

    // Navigate to login page
    this.router.navigate(['/login']).then(() => {
      // Show expiration dialog only if not a manual logout and no dialogs are already open
      if (!this.isManualLogout && this.dialog.openDialogs.length === 0) {
        this.dialog.open(NoAccessDialogComponent, {
          width: '550px',
          data: {
            title: 'Sesión expirada',
            message:
              'Su sesión ha expirado. Por favor, inicie sesión nuevamente.',
            confirmText: 'Cerrar',
            cancelText: 'Cancelar',
          },
        });
      }

      // Reset the flags after a short delay
      setTimeout(() => {
        this.isHandlingExpiration = false;
        this.isManualLogout = false; // Reset manual logout flag for next session
      }, 1000);
    });
  }

  /**
   * Starts monitoring for token expiration
   * Should be called after successful login
   */
  startTokenExpirationMonitoring(): void {
    // Clear any existing timeout first
    this.clearExpirationTimeout();

    // Reset manual logout flag when starting a new session
    this.isManualLogout = false;

    const remainingTime = this.getRemainingTokenTime();

    if (remainingTime <= 0) {
      this.handleTokenExpiration();
      return;
    }

    // Set timeout for when token expires
    this.timeoutId = window.setTimeout(() => {
      this.handleTokenExpiration();
    }, remainingTime);

    // console.log(
    //   `Token expiration monitoring started. Token expires in ${Math.round(
    //     remainingTime / (1000 * 60)
    //   )} minutes`
    // );
  }

  /**
   * Checks if user should be logged out due to token expiration
   * Should be called on app initialization
   */
  checkTokenOnAppStart(): boolean {
    if (!this.loginService.isLoggedIn()) {
      return false; // User not logged in, no need to check
    }

    if (this.isTokenExpired()) {
      this.handleTokenExpiration();
      return true; // Token was expired
    }

    // Token is still valid, start monitoring for expiration
    this.startTokenExpirationMonitoring();
    return false; // Token is valid
  }
}
